import { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import api from "../../services/api";
import ModalCustom from "./modal-template-default";
import Slct from "../Slct";
import Required from "../Required";
import useWrapper from "../../hooks/useWrapper";
import getCommercial from "../../util/getCommercial";
import { toast } from "react-toastify";
import Loading from "../Loading";
import Results from "../Results";
import useFormat from "../../hooks/utils/useFormat";
import { Currency, InputText } from "../Input";
import Button from "../Button";
import { validateForm } from "../ValidateForm";
import usePermission from "../../hooks/usePermission";
import useSuppliers from "../../hooks/api/useSuppliers";
import QuotaEditor from "../QuotaEditor";
import {
  PAYMENT_METHOD_OPTIONS,
  PIX_KEY_TYPE_LABEL,
  ACCOUNT_TYPE_LABEL,
  ACCOUNT_OWNERSHIP_LABEL,
  formatPixKey,
  formatDocument,
  supplierMethodOptions,
  supplierAccountOptions,
  supplierPixOptions,
  withEmptyOption,
  NO_ACCOUNTS_LABEL,
  NO_PIX_KEYS_LABEL,
} from "../../util/paymentMethods";

/**
 * Modal dedicado de negociação de pagamento (bills to pay).
 * Criação: gera a negociação (header) + parcelas.
 * Edição: dados gerais valem para o grupo; parcelas pagas ficam travadas e o
 * valor restante é redistribuído apenas nas parcelas em aberto.
 */
export default function ModalBillToPay(props) {
  const { show, onHide, negotiation, refreshCallback } = props;
  const isEdit = !!negotiation;

  const { config } = useWrapper();
  const { formatReal } = useFormat();
  const canCreate = usePermission("financialCompany", "create");
  const canUpdate = usePermission("financialCompany", "update");
  const canSubmit = isEdit ? canUpdate : canCreate;

  const suppliers = useSuppliers();

  const [selects, setselects] = useState({});
  const [description, setdescription] = useState("");
  const [costCenters, setcostCenters] = useState([]);
  const [commercials, setcommercials] = useState([]);
  const [values, setvalues] = useState({});
  const [valueNegotiation, setvalueNegotiation] = useState();
  const [editTotal, seteditTotal] = useState();
  const [quotaConfig, setquotaConfig] = useState({});
  const [quotas, setquotas] = useState([]);
  const [isLoadingStartup, setisLoadingStartup] = useState(true);
  const [isLoadingRequest, setisLoadingRequest] = useState(false);
  const isWaitingResponse = isLoadingStartup || isLoadingRequest;

  const [editNegotiation, seteditNegotiation] = useState(null);
  const negotiationBillToPays = isEdit ? editNegotiation?.billToPays : null;

  const lockedRows = isEdit
    ? negotiationBillToPays
        ?.filter(__bill => __bill.status === "pago")
        ?.map(__bill => ({
          label: `${__bill.quotas}/${editNegotiation?.total_quotas}`,
          value: __bill.value,
          due_date: __bill.due_date,
          description: __bill.description,
          status: __bill.status,
        })) || []
    : [];
  const paidSum = isEdit
    ? negotiationBillToPays
        ?.filter(__bill => __bill.status === "pago")
        ?.reduce((__acc, __bill) => __acc + (parseFloat(__bill.value) || 0), 0) || 0
    : 0;

  // Valor a distribuir nas parcelas em aberto
  const quotaResult = isEdit ? (editTotal || 0) - paidSum : valueNegotiation || 0;

  // Carregamento inicial
  useEffect(() => {
    if (!show) return;
    (async () => {
      setisLoadingStartup(true);
      try {
        suppliers.load();
        const { data: _cost_centers } = await api.get("cost-center?type=despesa", config);
        setcostCenters(
          _cost_centers?.map(__cost_center => ({
            value: __cost_center.id,
            label: __cost_center.name,
            expensePlan: __cost_center.expensePlan?.map(__expense_plan => ({
              value: __expense_plan.id,
              label: __expense_plan.name,
              expenses: __expense_plan.expenses?.map(__expense => ({
                value: __expense.id,
                label: __expense.name,
              })),
            })),
          })),
        );

        const _commercials = await getCommercial();
        setcommercials(
          _commercials.map(__group => ({
            label: __group.label,
            value: __group.value,
            trainees: __group.trainees?.map(__trainee => ({
              label: __trainee.name || __trainee.label,
              value: __trainee.id || __trainee.value,
            })),
          })),
        );

        if (isEdit) {
          // busca a negociação direto da API: o objeto vindo da listagem pode
          // estar desatualizado (ex: baixa/cancelamento acabou de acontecer e a
          // lista ainda não recarregou)
          const { data: _fresh } = await api.get(`bill-to-pay-negotiations/${negotiation.id}`, config);
          seteditNegotiation(_fresh);

          setselects({
            payment_for: _fresh?.payment_for,
            provider_id: _fresh?.provider_id,
            commercial_id: _fresh?.commercial_id,
            trainee_id: _fresh?.trainee_id,
            cost_center_id: _fresh?.cost_center_id,
            expense_plan_id: _fresh?.expense_plan_id,
            expense_id: _fresh?.expense_id,
            payment_account_id: _fresh?.payment_account_id,
            payment_pix_id: _fresh?.payment_pix_id,
          });
          setdescription(_fresh?.description || "");
          setvalues({
            payment_type: _fresh?.type_payment,
            payment_method: _fresh?.method_payment,
          });
          seteditTotal(_fresh?.value_total);
          const _open = _fresh?.billToPays?.filter(__bill => __bill.status === "a-pagar") || [];
          setquotaConfig({
            quota_value: _open.length || 1,
            first_quota_date: _open[0]?.due_date ? new Date(_open[0].due_date) : new Date(),
            quota_interval: null,
            has_entry: false,
          });
        } else {
          seteditNegotiation(null);
          setselects({});
          setdescription("");
          setvalues({});
          setvalueNegotiation();
          seteditTotal();
          setquotaConfig({ quota_value: 1, first_quota_date: null, quota_interval: null, has_entry: false });
        }
        setquotas([]);
      } catch (__error) {
        toast.error("Erro ao tentar carregar alguns dados");
        console.log("__error", __error);
      } finally {
        setisLoadingStartup(false);
      }
    })();
    // recarrega apenas na abertura do modal ou troca de negociação em edição
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, negotiation?.id]);

  const closeModal = () => {
    if (isWaitingResponse) return;
    onHide();
  };

  const sendNegotiation = async () => {
    if (!canSubmit) return;
    const _is_valid = validateForm(document.getElementById("modal_bill_to_pay"));
    if (!_is_valid) return;
    if (!(quotas?.length > 0)) {
      toast.error("Configure o parcelamento antes de salvar.");
      return;
    }
    if (quotas.some(__quota => !(__quota.value > 0) || !__quota.due_date)) {
      toast.error("Toda parcela precisa de valor e vencimento.");
      return;
    }

    setisLoadingRequest(true);
    try {
      const _installments = quotas.map(__quota => ({
        due_date: __quota.due_date,
        value: __quota.value,
        description: __quota.description || null,
        entry: !!__quota.is_entry,
      }));

      const _payload = {
        payment_for: selects?.payment_for,
        provider_id: selects?.payment_for === "empresa" ? selects?.provider_id : null,
        commercial_id: selects?.commercial_id || null,
        trainee_id: selects?.payment_for === "formando" ? selects?.trainee_id : null,
        cost_center_id: selects?.cost_center_id,
        expense_plan_id: selects?.expense_plan_id,
        expense_id: selects?.expense_id,
        description,
        type_payment: values?.payment_type,
        method_payment: values?.payment_method,
        payment_account_id: selects?.payment_for === "empresa" ? selects?.payment_account_id || null : null,
        payment_pix_id: selects?.payment_for === "empresa" ? effectivePixId : null,
        installments: _installments,
        ...(isEdit
          ? {
              value_total: editTotal,
            }
          : {
              value_raw: valueNegotiation,
              value_total: valueNegotiation,
              entry: !!quotaConfig?.has_entry,
            }),
      };

      if (isEdit) {
        await api.put(`bill-to-pay-negotiations/${negotiation.id}`, _payload, config);
        toast.success("Título de pagamento atualizado!");
      } else {
        await api.post("bill-to-pay-negotiations", _payload, config);
        toast.success("Título de pagamento gerado!");
      }
      refreshCallback && refreshCallback();
      onHide();
    } catch (__error) {
      const _message = __error?.response?.data?.error?.message;
      toast.error(_message || "Problemas ao salvar o título de pagamento.");
      console.log("__error", __error);
    } finally {
      setisLoadingRequest(false);
    }
  };

  const supplierOptions =
    suppliers?.data?.map(__supplier => ({
      value: __supplier.id,
      label: __supplier.name,
    })) || [];

  // o fornecedor completo (com payment/account/pixKeys) já vem no GET providers,
  // então método, conta e chave PIX saem do cadastro dele sem request extra
  const isForSupplier = selects?.payment_for === "empresa";
  const selectedSupplier = isForSupplier
    ? suppliers?.data?.find(__supplier => __supplier.id === selects?.provider_id)
    : null;
  const methodOptions = selectedSupplier
    ? supplierMethodOptions(selectedSupplier, { keepValue: isEdit ? values?.payment_method : null })
    : PAYMENT_METHOD_OPTIONS;
  const accountOptions = supplierAccountOptions(selectedSupplier);
  // agência/conta só é usada em transferência — nos outros métodos o campo é
  // ruído (PIX usa a chave, espécie/cartão/boleto não usam conta de destino)
  const showAccounts = values?.payment_method === "transferencia";
  const selectedAccount = selectedSupplier?.account?.find(__account => __account.id === selects?.payment_account_id);
  const pixOptions = supplierPixOptions(selectedSupplier);
  const showPix = values?.payment_method === "pix";
  // com uma chave só ela já vem escolhida, mas o select continua visível: o
  // gestor precisa ver qual chave o título vai registrar
  const effectivePixId = selects?.payment_pix_id || (pixOptions.length === 1 ? pixOptions[0].value : null);
  const selectedPix = selectedSupplier?.pixKeys?.find(__pix => __pix.id === effectivePixId);

  return (
    <ModalCustom show={show} onHide={closeModal} disableClose={isWaitingResponse} id="modal_bill_to_pay" size="xl">
      <ModalCustom.Header>
        <ModalCustom.Title>{isEdit ? "Editar Título de Pagamento" : "Novo Título de Pagamento"}</ModalCustom.Title>
      </ModalCustom.Header>
      <ModalCustom.Body>
        {!isLoadingStartup ? (
          <>
            <Row>
              <Col sm={4}>
                <span>
                  Pagamento referente à <Required />
                </span>
                <Slct
                  required={true}
                  options={[
                    { label: "Fornecedor", value: "empresa" },
                    { label: "Turma", value: "turma" },
                    { label: "Formando", value: "formando" },
                  ]}
                  value={selects?.payment_for}
                  isDisabled={isWaitingResponse}
                  onChange={__ev => {
                    setselects({
                      ...selects,
                      payment_for: __ev.value,
                      provider_id: null,
                      commercial_id: null,
                      trainee_id: null,
                      payment_account_id: null,
                      payment_pix_id: null,
                    });
                  }}
                  placeholder="..."
                />
              </Col>
              {selects?.payment_for === "empresa" && (
                <Col sm={4}>
                  <span>
                    Fornecedor <Required />
                  </span>
                  <Slct
                    required={true}
                    options={supplierOptions}
                    value={selects?.provider_id}
                    isDisabled={isWaitingResponse}
                    onChange={__ev => {
                      // método e conta pertencem ao cadastro do fornecedor
                      // anterior: só sobrevivem se o novo também os aceitar
                      const _supplier = suppliers?.data?.find(__s => __s.id === __ev.value);
                      const _methods = supplierMethodOptions(_supplier).map(__option => __option.value);
                      setselects({
                        ...selects,
                        provider_id: __ev.value,
                        payment_account_id: null,
                        payment_pix_id: null,
                      });
                      if (values?.payment_method && !_methods.includes(values.payment_method)) {
                        setvalues({ ...values, payment_method: null });
                      }
                    }}
                    placeholder="..."
                  />
                </Col>
              )}
              {selects?.payment_for === "empresa" && (
                <Col sm={4}>
                  <span>Turma relacionada (opcional)</span>
                  <Slct
                    options={[{ value: null, label: "-- Nenhuma --" }, ...(commercials || [])]}
                    value={selects?.commercial_id}
                    isDisabled={isWaitingResponse}
                    onChange={__ev => {
                      setselects({
                        ...selects,
                        commercial_id: __ev.value,
                      });
                    }}
                    placeholder="..."
                  />
                </Col>
              )}
              {(selects?.payment_for === "turma" || selects?.payment_for === "formando") && (
                <Col sm={4}>
                  <span>
                    Turma <Required />
                  </span>
                  <Slct
                    required={true}
                    options={commercials || []}
                    value={selects?.commercial_id}
                    isDisabled={isWaitingResponse}
                    onChange={__ev => {
                      setselects({
                        ...selects,
                        commercial_id: __ev.value,
                        trainee_id: null,
                      });
                    }}
                    placeholder="..."
                  />
                </Col>
              )}
              {selects?.payment_for === "formando" && (
                <Col sm={4}>
                  <span>
                    Formando <Required />
                  </span>
                  <Slct
                    required={true}
                    options={
                      commercials?.find(__commercial => __commercial.value === selects?.commercial_id)?.trainees || []
                    }
                    value={selects?.trainee_id}
                    isDisabled={isWaitingResponse || !selects?.commercial_id}
                    onChange={__ev => {
                      setselects({
                        ...selects,
                        trainee_id: __ev.value,
                      });
                    }}
                    placeholder="..."
                  />
                </Col>
              )}
            </Row>
            <Row className="mt-2">
              <Col>
                <span>
                  Centro de Custo <Required />
                </span>
                <Slct
                  required={true}
                  options={costCenters || []}
                  value={selects?.cost_center_id}
                  isDisabled={isWaitingResponse}
                  onChange={__ev => {
                    setselects({
                      ...selects,
                      cost_center_id: __ev.value,
                      expense_plan_id: null,
                      expense_id: null,
                    });
                  }}
                  placeholder="..."
                />
              </Col>
              <Col>
                <span>
                  Plano de Conta <Required />
                </span>
                <Slct
                  required={true}
                  options={
                    selects?.cost_center_id
                      ? costCenters?.find(__cost_center => __cost_center.value === selects.cost_center_id)
                          ?.expensePlan || []
                      : []
                  }
                  value={selects?.expense_plan_id}
                  isDisabled={isWaitingResponse}
                  onChange={__ev => {
                    setselects({
                      ...selects,
                      expense_plan_id: __ev.value,
                      expense_id: null,
                    });
                  }}
                  placeholder="..."
                />
              </Col>
              <Col>
                <span>
                  Conta <Required />
                </span>
                <Slct
                  required={true}
                  options={
                    selects?.expense_plan_id
                      ? costCenters
                          ?.find(__cost_center => __cost_center.value === selects.cost_center_id)
                          ?.expensePlan?.find(__expense_plan => __expense_plan.value === selects.expense_plan_id)
                          ?.expenses || []
                      : []
                  }
                  value={selects?.expense_id}
                  isDisabled={isWaitingResponse}
                  onChange={__ev => {
                    setselects({
                      ...selects,
                      expense_id: __ev.value,
                    });
                  }}
                  placeholder="..."
                />
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <span>
                  Descrição <Required />
                </span>
                <InputText
                  required={true}
                  disabled={isWaitingResponse}
                  value={description}
                  onChange={__ev => setdescription(__ev.target.value)}
                  placeholder="Ex: Aluguel do salão de festas"
                />
              </Col>
            </Row>
            <Row className="mt-2">
              <Col sm={4}>
                <span>
                  Valor Total <Required />
                </span>
                <Currency
                  required={true}
                  allowNegative={false}
                  disabled={isWaitingResponse}
                  value={isEdit ? editTotal : valueNegotiation}
                  valueChange={__ev => {
                    isEdit ? seteditTotal(__ev) : setvalueNegotiation(__ev);
                  }}
                />
              </Col>
              <Col sm={4}>
                <span>
                  Tipo de Pagamento <Required />
                </span>
                <Slct
                  isDisabled={isWaitingResponse}
                  required={true}
                  options={[
                    { value: "avista", label: "À Vista" },
                    { value: "parcelado", label: "A Prazo / Parcelado" },
                  ]}
                  value={values?.payment_type}
                  onChange={__ev => {
                    setvalues({
                      ...values,
                      payment_type: __ev.value,
                    });
                    setquotaConfig({
                      ...quotaConfig,
                      quota_value: 1,
                      has_entry: false,
                      first_quota_date: __ev.value === "avista" ? new Date() : quotaConfig?.first_quota_date,
                    });
                  }}
                  placeholder="..."
                />
              </Col>
              <Col sm={4}>
                <span>
                  Método do Pagamento <Required />
                </span>
                <Slct
                  isDisabled={isWaitingResponse}
                  required={true}
                  options={methodOptions}
                  value={values?.payment_method}
                  onChange={__ev => {
                    setvalues({
                      ...values,
                      payment_method: __ev.value,
                    });
                    // conta de destino só faz sentido em transferência: sair do
                    // método limpa a conta para o título não congelar
                    // agência/conta de um pagamento que não usa nenhuma
                    if (__ev.value !== "transferencia" && selects?.payment_account_id) {
                      setselects({ ...selects, payment_account_id: null });
                    }
                  }}
                  placeholder="..."
                />
              </Col>
            </Row>
            {isForSupplier && !!selectedSupplier && (
              <Row>
                {showAccounts && (
                  <Col sm={4} className="mt-3">
                    <span>
                      Conta para depósito <Required />
                    </span>
                    <Slct
                      required={true}
                      isDisabled={isWaitingResponse}
                      options={withEmptyOption(accountOptions, NO_ACCOUNTS_LABEL)}
                      value={selects?.payment_account_id || null}
                      onChange={__ev => {
                        setselects({
                          ...selects,
                          payment_account_id: __ev.value,
                        });
                      }}
                      placeholder="..."
                    />
                    {!!selectedAccount && (
                      <p className="small-label mb-0 mt-2">
                        <b>Banco:</b> {selectedAccount.bank}
                        <br />
                        <b>Agência:</b> {selectedAccount.agency}
                        <br />
                        <b>Conta:</b> {selectedAccount.account}
                        {!!selectedAccount.type && ` (${ACCOUNT_TYPE_LABEL[selectedAccount.type] || selectedAccount.type})`}
                        {!!selectedAccount.ownership && (
                          <>
                            <br />
                            <b>Titularidade:</b>{" "}
                            {ACCOUNT_OWNERSHIP_LABEL[selectedAccount.ownership] || selectedAccount.ownership}
                          </>
                        )}
                        {!!selectedAccount.document && (
                          <>
                            <br />
                            <b>{selectedAccount.ownership === "cpf" ? "CPF" : "CNPJ"}:</b>{" "}
                            {formatDocument(selectedAccount.ownership, selectedAccount.document)}
                          </>
                        )}
                      </p>
                    )}
                  </Col>
                )}
                {showPix && (
                  <Col sm={4} className="mt-3">
                    <span>
                      Chave PIX <Required />
                    </span>
                    <Slct
                      required={true}
                      isDisabled={isWaitingResponse}
                      options={withEmptyOption(pixOptions, NO_PIX_KEYS_LABEL)}
                      value={effectivePixId}
                      onChange={__ev => {
                        setselects({
                          ...selects,
                          payment_pix_id: __ev.value,
                        });
                      }}
                      placeholder="..."
                    />
                    {selectedPix ? (
                      <p className="small-label mb-0 mt-2">
                        <b>{PIX_KEY_TYPE_LABEL[selectedPix.key_type] || "Chave"}:</b>{" "}
                        {formatPixKey(selectedPix.key_type, selectedPix.pix_key)}
                        {!!selectedPix.bank && (
                          <>
                            <br />
                            <b>Banco:</b> {selectedPix.bank}
                          </>
                        )}
                        {!!selectedPix.name && (
                          <>
                            <br />
                            <b>Titular:</b> {selectedPix.name}
                          </>
                        )}
                      </p>
                    ) : (
                      pixOptions.length > 0 && (
                        <p className="small-label mb-0 mt-2">Selecione a chave PIX que será usada no pagamento.</p>
                      )
                    )}
                  </Col>
                )}
              </Row>
            )}
            <Row className="mt-3">
              <Col sm={6}>
                <p className="mini-title mb-2">
                  <b>Observações:</b>
                </p>
                <p className="small-label mb-2">
                  Ao menos uma parcela não poderá ser editada, pois ela receberá o restante do Valor Total.
                </p>
                <p className="small-label mb-2">
                  Havendo desconto ou taxa, inclua o detalhe na Descrição — o Valor Total já deve refletir o valor
                  final.
                </p>
                {isEdit && lockedRows.length > 0 && (
                  <p className="small-label mb-2">
                    As parcelas pagas não são alteradas. O valor a distribuir é o novo Valor Total menos a soma das
                    parcelas pagas.
                  </p>
                )}
              </Col>
              <Col sm={6}>
                <Results
                  rows={[
                    {
                      label: <b>Valor Total</b>,
                      value: <b>{formatReal(isEdit ? editTotal : valueNegotiation)}</b>,
                      type: "dark",
                    },
                    ...(isEdit
                      ? [
                          {
                            label: "Parcelas Pagas",
                            value: `- ${formatReal(paidSum)}`,
                          },
                          {
                            label: <b>A Distribuir</b>,
                            value: <b>{formatReal(quotaResult)}</b>,
                            type: "dark",
                          },
                        ]
                      : []),
                  ]}
                />
              </Col>
            </Row>
            {quotaResult > 0 && !!values?.payment_type && (
              <QuotaEditor
                result={quotaResult}
                quotas={quotas}
                setquotas={setquotas}
                config={quotaConfig}
                setconfig={setquotaConfig}
                paymentType={values?.payment_type}
                lockedRows={lockedRows}
                disabled={isWaitingResponse}
              />
            )}
          </>
        ) : (
          <Loading className="mb-2" />
        )}
      </ModalCustom.Body>
      {canSubmit && (
        <ModalCustom.Footer>
          <Button disabled={isWaitingResponse} color="primary_ghost" onClick={closeModal}>
            Cancelar
          </Button>
          <Button disabled={isWaitingResponse} loading={isLoadingRequest} onClick={sendNegotiation}>
            {isEdit ? "Salvar Alterações" : "Criar Título de Pagamento"}
          </Button>
        </ModalCustom.Footer>
      )}
    </ModalCustom>
  );
}
